import { useState, useEffect, useRef, type ChangeEvent } from 'react'
import { useSchoolProfile, type SaveFields } from '@/hooks/useSchoolProfile'
import { FormField, SelectField } from '@/components/ui/FormField'
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete'

const BUSINESS_STRUCTURE_LABELS: Record<string, string> = {
  sole_proprietorship: 'Sole proprietorship',
  partnership: 'Partnership',
  corporation: 'Corporation',
  other: 'Other',
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  business_registration: 'Business registration',
  mto_licence: 'MTO licence',
  other: 'Other',
}

const VERIFICATION_STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  unverified: { bg: 'rgba(27,42,74,0.07)', color: 'rgba(27,42,74,0.5)', label: 'Not yet reviewed' },
  pending_review: { bg: 'rgba(212,151,43,0.12)', color: '#9A6C0E', label: 'Pending review' },
  verified: { bg: 'rgba(15,124,108,0.1)', color: '#0F7C6C', label: 'Verified' },
  rejected: { bg: 'rgba(224,102,59,0.1)', color: '#C0522A', label: 'Rejected' },
}

interface SchoolProfileFormProps {
  schoolId: string
  /** 'wizard' hides the completeness summary chrome the Settings page shows; both call
   * the exact same save path. */
  variant?: 'wizard' | 'settings'
  onSaved?: (complete: boolean) => void
}

/**
 * The shared school business-profile field set (migration 080-082) — used by both the
 * required onboarding wizard and the Settings "School profile" tab, so there is exactly
 * one implementation of these fields and their validation messaging. Calls
 * update_school_profile() via useSchoolProfile(), which always succeeds once authorized
 * and reports completeness separately (complete/missing_fields) rather than a hard
 * rejection — so this form can be saved mid-way through the wizard without losing data.
 */
export function SchoolProfileForm({ schoolId, variant = 'settings', onSaved }: SchoolProfileFormProps) {
  const {
    school, documents, loading, error, saving, uploadingLogo, uploadingDocument,
    save, uploadLogo, logoUrl, uploadDocument, documentUrl,
  } = useSchoolProfile(schoolId)

  const [fields, setFields] = useState<SaveFields>({
    legalBusinessName: '', tradingName: '', businessStructure: '',
    businessRegistrationNumber: '', businessEmail: '', businessPhone: '',
    businessAddress: '', businessAddressPlaceId: null, isMtoLicensed: null,
    mtoLicenceNumber: '', mtoLicenceExpiryDate: '', websiteUrl: '',
  })
  const [saveError, setSaveError] = useState('')
  const [saveNotice, setSaveNotice] = useState('')
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [logoError, setLogoError] = useState('')
  const [docError, setDocError] = useState('')
  const [docTitle, setDocTitle] = useState('')
  const [docType, setDocType] = useState<'business_registration' | 'mto_licence' | 'other'>('business_registration')

  // Populate fields from the fetched school ONCE per school id, never again — a document
  // or logo upload (or save itself) re-fetches `school` (a new object reference, same
  // schoolId) via fetchAll(), and re-running this on every such change was wiping out
  // whatever the admin had typed but not yet saved (the actual cause of "all information
  // was lost" after uploading a document mid-wizard). Switching to a genuinely different
  // school (Settings, multi-school admin) still re-syncs, since the ref updates then too.
  const syncedSchoolIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!school || syncedSchoolIdRef.current === school.id) return
    syncedSchoolIdRef.current = school.id
    setFields({
      legalBusinessName: school.legalBusinessName ?? '',
      tradingName: school.name ?? '',
      businessStructure: school.businessStructure ?? '',
      businessRegistrationNumber: school.businessRegistrationNumber ?? '',
      businessEmail: school.businessEmail ?? '',
      businessPhone: school.businessPhone ?? '',
      businessAddress: school.businessAddress ?? '',
      businessAddressPlaceId: school.businessAddressPlaceId,
      isMtoLicensed: school.isMtoLicensed,
      mtoLicenceNumber: school.mtoLicenceNumber ?? '',
      mtoLicenceExpiryDate: school.mtoLicenceExpiryDate ?? '',
      websiteUrl: school.websiteUrl ?? '',
    })
  }, [school])

  const handleSave = async () => {
    setSaveError(''); setSaveNotice(''); setMissingFields([])
    const result = await save(fields)
    if (!result.ok) {
      setSaveError('Something went wrong saving your profile. Please try again.')
      return
    }
    setMissingFields(result.missing_fields ?? [])
    setSaveNotice(result.complete ? 'Saved.' : 'Saved — some required fields are still missing below.')
    onSaved?.(!!result.complete)
  }

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLogoError('')
    const result = await uploadLogo(file)
    if (!result.ok) setLogoError('Could not upload the logo. Please try a different image.')
    e.target.value = ''
  }

  const handleDocumentChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setDocError('')
    if (!docTitle.trim()) { setDocError('Enter a title for this document first.'); e.target.value = ''; return }
    const result = await uploadDocument(file, docType, docTitle.trim())
    if (!result.ok) setDocError('Could not upload this document. PDF, JPG, or PNG only, up to 10 MB.')
    else setDocTitle('')
    e.target.value = ''
  }

  const openDocument = async (documentId: string) => {
    const url = await documentUrl(documentId)
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (loading) return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  if (error || !school) return <div className='banner-error'>{error ?? 'Could not load this school.'}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 14 }}>Business identity</div>
        <div className='fg2' style={{ marginBottom: 12 }}>
          <FormField label='Legal business name' value={fields.legalBusinessName} onChange={e => setFields(f => ({ ...f, legalBusinessName: e.target.value }))} placeholder='1234567 Ontario Inc.' required />
          <FormField label='Trading / operating name' value={fields.tradingName} onChange={e => setFields(f => ({ ...f, tradingName: e.target.value }))} placeholder='Maple Grove Driving School' required />
        </div>
        <div className='fg2'>
          <SelectField label='Business structure' value={fields.businessStructure} onChange={e => setFields(f => ({ ...f, businessStructure: e.target.value as SaveFields['businessStructure'] }))} required>
            <option value=''>Select…</option>
            {Object.entries(BUSINESS_STRUCTURE_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </SelectField>
          <FormField label='Business registration number' value={fields.businessRegistrationNumber} onChange={e => setFields(f => ({ ...f, businessRegistrationNumber: e.target.value }))} placeholder='As applicable' />
        </div>
      </div>

      <div>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 14 }}>Contact & address</div>
        <div className='fg2' style={{ marginBottom: 12 }}>
          <FormField label='Business email' type='email' value={fields.businessEmail} onChange={e => setFields(f => ({ ...f, businessEmail: e.target.value }))} placeholder='office@school.com' required />
          <FormField label='Business phone' value={fields.businessPhone} onChange={e => setFields(f => ({ ...f, businessPhone: e.target.value }))} placeholder='(416) 555-0100' required />
        </div>
        <AddressAutocomplete
          label='Business address'
          value={fields.businessAddress}
          onChange={(address, placeId) => setFields(f => ({ ...f, businessAddress: address, businessAddressPlaceId: placeId }))}
        />
      </div>

      <div>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 4 }}>MTO licensing</div>
        <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 12 }}>
          Only MTO-approved driving schools need a licence number and expiry date.
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          {([['Licensed', true], ['Not MTO-licensed', false]] as const).map(([label, val]) => (
            <div
              key={label}
              onClick={() => setFields(f => ({ ...f, isMtoLicensed: val }))}
              style={{
                cursor: 'pointer', padding: '8px 16px', borderRadius: 9,
                border: `2px solid ${fields.isMtoLicensed === val ? '#0F7C6C' : 'rgba(27,42,74,0.12)'}`,
                background: fields.isMtoLicensed === val ? 'rgba(15,124,108,0.07)' : '#fff',
                font: '500 13px Inter, sans-serif', color: '#1B2A4A',
              }}
            >
              {label}
            </div>
          ))}
        </div>
        {fields.isMtoLicensed === true && (
          <div className='fg2' style={{ marginBottom: 8 }}>
            <FormField label='MTO school licence #' value={fields.mtoLicenceNumber} onChange={e => setFields(f => ({ ...f, mtoLicenceNumber: e.target.value }))} placeholder='DS-000000' required />
            <FormField label='Licence expiry date' type='date' value={fields.mtoLicenceExpiryDate} onChange={e => setFields(f => ({ ...f, mtoLicenceExpiryDate: e.target.value }))} required />
          </div>
        )}
        {school.isMtoLicensed && (
          <div style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: 20,
            background: VERIFICATION_STATUS_STYLE[school.mtoLicenceVerificationStatus].bg,
            color: VERIFICATION_STATUS_STYLE[school.mtoLicenceVerificationStatus].color,
            font: '600 11.5px Inter, sans-serif',
          }}>
            {VERIFICATION_STATUS_STYLE[school.mtoLicenceVerificationStatus].label}
          </div>
        )}
      </div>

      <div>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 12 }}>Branding (optional)</div>
        <FormField label='Website' value={fields.websiteUrl} onChange={e => setFields(f => ({ ...f, websiteUrl: e.target.value }))} placeholder='https://yourschool.com' />
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 14 }}>
          {school.logoPath && (
            <img src={logoUrl(school.logoPath) ?? undefined} alt='School logo' style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: '1px solid rgba(27,42,74,0.1)' }} />
          )}
          <label style={{
            cursor: 'pointer', padding: '9px 16px', borderRadius: 9, border: '1px solid rgba(27,42,74,0.15)',
            font: '500 12.5px Inter, sans-serif', color: '#1B2A4A',
          }}>
            {uploadingLogo ? 'Uploading…' : school.logoPath ? 'Replace logo' : 'Upload logo'}
            <input type='file' accept='image/*' onChange={handleLogoChange} disabled={uploadingLogo} style={{ display: 'none' }} />
          </label>
        </div>
        {logoError && <div className='banner-error' style={{ marginTop: 10 }}>{logoError}</div>}
      </div>

      <div>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 4 }}>Business documents (as applicable)</div>
        <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 12 }}>
          Certificate of incorporation, MTO licence, or other supporting documents.
        </div>
        {documents.map(doc => (
          <div key={doc.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            padding: '8px 0', borderBottom: '1px solid rgba(27,42,74,0.05)',
          }}>
            <div>
              <div style={{ font: '500 13px Inter, sans-serif', color: '#1B2A4A' }}>{doc.title}</div>
              <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>{DOCUMENT_TYPE_LABELS[doc.documentType]}</div>
            </div>
            <button
              onClick={() => openDocument(doc.id)}
              style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer' }}
            >
              View
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 160px' }}>
            <FormField label='Document title' value={docTitle} onChange={e => setDocTitle(e.target.value)} placeholder='Certificate of Incorporation' />
          </div>
          <div style={{ flex: '0 0 170px' }}>
            <SelectField label='Type' value={docType} onChange={e => setDocType(e.target.value as typeof docType)}>
              {Object.entries(DOCUMENT_TYPE_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </SelectField>
          </div>
          <label style={{
            cursor: 'pointer', padding: '10px 16px', borderRadius: 9, border: '1px solid rgba(27,42,74,0.15)',
            font: '500 12.5px Inter, sans-serif', color: '#1B2A4A', flexShrink: 0,
          }}>
            {uploadingDocument ? 'Uploading…' : 'Upload'}
            <input type='file' accept='.pdf,image/jpeg,image/png' onChange={handleDocumentChange} disabled={uploadingDocument} style={{ display: 'none' }} />
          </label>
        </div>
        {docError && <div className='banner-error' style={{ marginTop: 10 }}>{docError}</div>}
      </div>

      {missingFields.length > 0 && variant === 'wizard' && (
        <div className='banner-error'>
          Still needed: {missingFields.join(', ')}
        </div>
      )}
      {saveError && <div className='banner-error'>{saveError}</div>}
      {saveNotice && (
        <div style={{
          padding: '10px 14px', borderRadius: 9,
          background: 'rgba(15,124,108,0.07)', border: '1px solid rgba(15,124,108,0.2)',
          font: '400 12.5px Inter, sans-serif', color: '#1B2A4A',
        }}>
          {saveNotice}
        </div>
      )}

      <button className='btn-teal' onClick={handleSave} disabled={saving} style={{ width: 'auto', padding: '11px 22px', alignSelf: 'flex-start' }}>
        {saving ? 'Saving…' : variant === 'wizard' ? 'Save & continue' : 'Save changes'}
      </button>
    </div>
  )
}
